/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetInstanceRequest,
  GetInstanceSecurity,
} from "../models/operations/getinstance.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildInstanceQuery,
  InstanceQueryData,
  prefetchInstance,
  queryKeyInstance,
} from "./instance.core.js";
export {
  buildInstanceQuery,
  type InstanceQueryData,
  prefetchInstance,
  queryKeyInstance,
};

export type InstanceQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getInstance instances
 *
 * @remarks
 * Load all relevant data for an instance of a toolset and environment
 */
export function useInstance(
  request: GetInstanceRequest,
  security?: GetInstanceSecurity | undefined,
  options?: QueryHookOptions<InstanceQueryData, InstanceQueryError>,
): UseQueryResult<InstanceQueryData, InstanceQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildInstanceQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getInstance instances
 *
 * @remarks
 * Load all relevant data for an instance of a toolset and environment
 */
export function useInstanceSuspense(
  request: GetInstanceRequest,
  security?: GetInstanceSecurity | undefined,
  options?: SuspenseQueryHookOptions<InstanceQueryData, InstanceQueryError>,
): UseSuspenseQueryResult<InstanceQueryData, InstanceQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildInstanceQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setInstanceData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      toolsetSlug: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
      gramKey?: string | undefined;
      gramChatSession?: string | undefined;
    },
  ],
  data: InstanceQueryData,
): InstanceQueryData | undefined {
  const key = queryKeyInstance(...queryKeyBase);

  return client.setQueryData<InstanceQueryData>(key, data);
}

export function invalidateInstance(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      toolsetSlug: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
      gramKey?: string | undefined;
      gramChatSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "instances", "getBySlug", ...queryKeyBase],
  });
}

export function invalidateAllInstance(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "instances", "getBySlug"],
  });
}
